/**
 * REMOVABLE WORKAROUND for legacy CMS-authored <script> tags that gate
 * their initialization on `document.addEventListener("DOMContentLoaded", ...)`
 * / `window.addEventListener("load", ...)` without checking whether those
 * events already fired (e.g. the homepage banner carousel's init script).
 *
 * useReplayCmsScripts always runs scripts after hydration, when the real
 * DOMContentLoaded/load events have long since fired — so a plain
 * `addEventListener` registration for them would never call back.
 *
 * This patches `addEventListener` on document/window. Every call is still
 * registered natively, exactly as it would have been; on top of that, a
 * registration for one of these two events made after the event already
 * fired also runs on the next microtask instead of waiting forever. Any
 * other event, and these two before they've fired, behave as if the patch
 * weren't there.
 *
 * useReplayCmsScripts installs it right before a script that might register
 * one of these listeners runs, and releases it as soon as that script is
 * known to be done — scoped to one script, not the whole replay chain, since
 * the chain can be network-bound end to end and every caller of
 * addEventListener in the app is affected while this patch is installed.
 *
 * To remove this workaround entirely: delete this file, delete this file's
 * test, and remove the `patchDomReadyEvents` import/call (and the
 * `openWindow`/`closeWindow` plumbing built around it) from
 * useReplayCmsScripts.hook.ts.
 */

type PatchTarget = Document | Window;
export type ReadyEvent = "DOMContentLoaded" | "load";
type NativeAddEventListener = PatchTarget["addEventListener"];
type ListenerErrorCallback = (eventName: ReadyEvent, error: Error) => void;

type Restore = {
  original: NativeAddEventListener;
  // Whether the target already had an own addEventListener before we patched
  // it. When it didn't, restoring means deleting our own property so lookups
  // go back to EventTarget.prototype instead of being shadowed forever.
  hadOwn: boolean;
};

const originals = new WeakMap<PatchTarget, Restore>();
let activeCount = 0;

// A Set rather than a single slot: patchDomReadyEvents is ref-counted and can
// have more than one concurrent caller (e.g. a CMS page's `content` and
// `after_content` replay chains both with an open window at the same time),
// each wanting its own errors. Only the first caller's install actually runs
// (see activeCount below), so every caller's callback needs to be reachable
// from that one shared installation, not just the first one's.
const listenerErrorCallbacks = new Set<ListenerErrorCallback>();

/**
 * Deliberately kept as a readyState heuristic. The spec sets readyState to
 * "interactive" *before* dispatching DOMContentLoaded, so there is a brief
 * window where this returns true while the real event is still pending — the
 * listener then runs twice. Tracking the real event with a module-level flag
 * would trade that for a worse failure: a module imported during that same
 * window would never see the event and the listener would never run at all.
 * Firing twice is the safe direction to be wrong in; silence is the bug this
 * whole mechanism exists to remove.
 */
function alreadyFired(eventName: ReadyEvent): boolean {
  return eventName === "DOMContentLoaded"
    ? document.readyState !== "loading"
    : document.readyState === "complete";
}

function normalizeOptions(
  options?: boolean | AddEventListenerOptions,
): AddEventListenerOptions | undefined {
  return typeof options === "object" && options !== null ? options : undefined;
}

function installPatch(target: PatchTarget, eventName: ReadyEvent): void {
  // Kept unbound (not .bind()'d) so restore() can put back this exact
  // function reference instead of an ever-growing chain of wrappers.
  const original = target.addEventListener;
  originals.set(target, {
    original,
    hadOwn: Object.prototype.hasOwnProperty.call(target, "addEventListener"),
  });

  target.addEventListener = ((
    type: string,
    listener: EventListenerOrEventListenerObject,
    options?: boolean | AddEventListenerOptions,
  ) => {
    // Always register for real. Short-circuiting used to leave the listener
    // unregistered, which silently broke removeEventListener, `once` and
    // `capture` for every caller that happened to run inside the window.
    original.call(target, type, listener, options);

    if (type !== eventName || !alreadyFired(eventName)) return;

    // `signal` is the one option the synthetic dispatch has to honour by
    // hand, since it never goes through the native dispatch path. A
    // removeEventListener call landing before the microtask is NOT observable
    // through the DOM API and still fires — known limitation, deliberately
    // not worth patching a second global for.
    const opts = normalizeOptions(options);
    if (opts?.signal?.aborted) return;

    queueMicrotask(() => {
      if (opts?.signal?.aborted) return;
      // A synthetic `new Event()` that never goes through dispatchEvent has
      // target/currentTarget/srcElement null and eventPhase NONE — a listener
      // reading any of those (e.g. `event.currentTarget.querySelector(...)`)
      // would throw. Defining them as own properties shadows the getters on
      // Event.prototype, mirroring what a real dispatch would set: target is
      // always the document (DOMContentLoaded and load both target it),
      // currentTarget is wherever this listener is registered. isTrusted
      // stays false and can't be forged — the one divergence left standing.
      const event = new Event(eventName);
      Object.defineProperties(event, {
        target: { value: document, configurable: true },
        srcElement: { value: document, configurable: true },
        currentTarget: { value: target, configurable: true },
        eventPhase: { value: Event.AT_TARGET, configurable: true },
      });
      try {
        if (typeof listener === "function") listener.call(target, event);
        else listener.handleEvent(event);
      } catch (error) {
        // A real dispatch isolates listener errors; thrown inside a microtask
        // this would become an unhandled error attributed to the app.
        console.error("[cms] replayed DOM-ready listener threw", error);
        // There's no way to tell which caller's script registered the
        // listener that threw — notify every currently active caller. In
        // practice this app only ever has at most two concurrent replays for
        // the same CMS page (content + after_content), sharing the same
        // cmsKey, so a double report is harmless, not a misattribution.
        const normalized =
          error instanceof Error ? error : new Error(String(error));
        listenerErrorCallbacks.forEach((callback) =>
          callback(eventName, normalized),
        );
      } finally {
        // A real dispatch resets these once every listener has run; do the
        // same so a reference to this event kept past the call doesn't read
        // as if still mid-dispatch.
        Object.defineProperties(event, {
          currentTarget: { value: null, configurable: true },
          eventPhase: { value: Event.NONE, configurable: true },
        });
      }
    });
  }) as NativeAddEventListener;
}

function uninstallPatch(target: PatchTarget): void {
  const restore = originals.get(target);
  if (!restore) return;

  if (restore.hadOwn) target.addEventListener = restore.original;
  else delete (target as { addEventListener?: unknown }).addEventListener;

  originals.delete(target);
}

/**
 * Installs the patch (ref-counted, safe to call from multiple concurrent
 * replays) and returns a `release` function. Call `release` exactly once
 * when this replay's script chain finishes or unmounts; the real
 * addEventListener is restored once every caller has released.
 *
 * `onListenerError` is called whenever a replayed listener throws while this
 * caller's window is open, so it can be reported (e.g. to Datadog). It can't
 * distinguish which script's window was open when the throw happened if
 * multiple are open at once — see the comment above `listenerErrorCallbacks`.
 */
export function patchDomReadyEvents(
  onListenerError?: ListenerErrorCallback,
): () => void {
  if (activeCount === 0) {
    installPatch(document, "DOMContentLoaded");
    installPatch(window, "load");
  }
  activeCount++;
  if (onListenerError) listenerErrorCallbacks.add(onListenerError);

  let released = false;
  return function release() {
    if (released) return;
    released = true;
    activeCount--;
    if (onListenerError) listenerErrorCallbacks.delete(onListenerError);
    if (activeCount === 0) {
      uninstallPatch(document);
      uninstallPatch(window);
    }
  };
}
